import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Sparkles } from "lucide-react";
import {
  checkoutErrorCopy,
  isPriceNotFoundError,
  readFunctionErrorMessage,
} from "@/lib/checkoutErrors";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { useToast } from "@/hooks/use-toast";
import { formatTrialEnd, trialBillingNote } from "@/lib/trial";
import { BillingCycleToggle } from "@/components/BillingCycleToggle";
import { getRefCode } from "@/lib/affiliate";
import {
  TIERS,
  annualSavingsLabel,
  effectiveMonthlyAmount,
  formatTierPrice,
  priceIdFor,
  tierCycle,
  type BillingCycle,
  type Tier,
} from "@/lib/tiers";

interface Props {
  open: boolean;
  onClose: () => void;
  featureLabel?: string;
}

const FEATURES = [
  "Unlimited meal plans",
  "Unlimited workout plans",
  "Food scanner with macro tracking",
  "AI chat coaching",
  "AI body scan and progress tracking",
  "IFBB Posing Coach (Pro only)",
];

type PlanId = Tier;

export function PaywallModal({ open, onClose, featureLabel }: Props) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState<PlanId | null>(null);
  const [cycle, setCycle] = useState<BillingCycle>("monthly");
  const trialEnd = formatTrialEnd();
  const unit = cycle === "yearly" ? "/ year AUD after trial" : "/ month AUD after trial";

  const startCheckout = async (plan: PlanId) => {
    if (loadingPlan) return;
    setLoadingPlan(plan);
    const requestedPriceId = priceIdFor(plan, cycle);
    try {
      const env = getStripeEnvironment();
      const origin = window.location.origin;
      const { data, error } = await supabase.functions.invoke(
        "create-subscription-checkout",
        {
          body: {
            priceId: requestedPriceId,
            environment: env,
            affiliate_code: getRefCode(),
        successUrl: `${origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}&env=${env}`,
            cancelUrl: `${origin}/app?checkout=cancelled`,
          },
        },
      );
      if (error || !data?.url) {
        const message = await readFunctionErrorMessage(error, data);
        throw new Error(message || "Failed to start checkout");
      }
      window.location.href = data.url as string;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      console.error("[paywall] checkout failed:", message);
      // A missing Stripe price is a hard configuration failure — send the
      // user to the friendly explainer instead of showing a raw 404 toast.
      if (isPriceNotFoundError(message)) {
        onClose();
        navigate(
          `/checkout/unavailable?reason=${encodeURIComponent(message)}&plan=${encodeURIComponent(requestedPriceId)}`,
        );
        setLoadingPlan(null);
        return;
      }
      toast({
        title: "Couldn't start checkout",
        description: checkoutErrorCopy("generic").body,
        variant: "destructive",
      });
      setLoadingPlan(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !loadingPlan && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0">
        <div className="px-6 pt-6 pb-4 text-center bg-gradient-to-b from-primary/10 to-transparent">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary/15 mb-3">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-2xl font-bold text-foreground">
            Start your seven day free trial
          </DialogTitle>
          <DialogDescription className="mt-1 text-base text-foreground/90">
            Flat pricing, no increases. Full access
            {featureLabel ? `, including ${featureLabel}` : ""} — free until{" "}
            {trialEnd}. You're not charged today.
          </DialogDescription>
        </div>

        <div className="flex justify-center px-6 pb-4">
          <BillingCycleToggle value={cycle} onChange={setCycle} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-6 pb-2">
          {(["standard", "pro"] as Tier[]).map((tier) => {
            const def = tierCycle(tier, cycle);
            return (
              <PlanButton
                key={tier}
                label={TIERS[tier].name}
                price={formatTierPrice(def.unitAmount)}
                compareAt={
                  def.compareAtAmount
                    ? formatTierPrice(def.compareAtAmount)
                    : undefined
                }
                unit={unit}
                note={
                  cycle === "yearly"
                    ? `${annualSavingsLabel(tier)} · ${formatTierPrice(
                        effectiveMonthlyAmount(tier),
                      )}/month equivalent`
                    : undefined
                }
                badge={tier === "pro" ? "Posing Coach" : null}
                highlight={tier === "pro"}
                loading={loadingPlan === tier}
                disabled={loadingPlan !== null && loadingPlan !== tier}
                onClick={() => startCheckout(tier)}
              />
            );
          })}
        </div>

        <div className="px-6 pb-2 pt-2">
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-sm">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                <span className="text-foreground font-medium">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="px-6 pb-6 pt-4 flex flex-col items-center gap-2">
          <p className="text-xs text-foreground/90 font-medium text-center">
            {trialBillingNote()} You can cancel from Account → Coach AI
            subscription.
          </p>
          <button
            onClick={onClose}
            disabled={loadingPlan !== null}
            className="text-xs text-foreground/80 hover:text-foreground transition disabled:opacity-50 font-medium"
          >
            No thanks, maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PlanButton({
  label,
  price,
  compareAt,
  unit,
  note,
  badge,
  highlight = false,
  loading,
  disabled,
  onClick,
}: {
  label: string;
  price: string;
  compareAt?: string;
  unit: string;
  note?: string;
  badge: string | null;
  highlight?: boolean;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={`Start seven day free trial, then ${label} at ${price} ${unit}`}
      className={`relative rounded-xl border p-4 text-left transition hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed ${
        highlight
          ? "border-primary bg-primary/5 hover:bg-primary/10"
          : "border-border bg-card hover:bg-accent/30"
      }`}
    >
      {badge && (
        <span className="absolute -top-2 right-3 text-[10px] font-semibold uppercase tracking-wide bg-primary text-primary-foreground rounded-full px-2 py-0.5">
          {badge}
        </span>
      )}
      <p className="text-sm font-medium text-foreground/90">{label}</p>
      <p className="mt-1 flex items-baseline gap-1">
        {compareAt && (
          <span className="text-lg font-semibold text-foreground/70 line-through">
            {compareAt}
          </span>
        )}
        <span className="text-3xl font-bold">{price}</span>
        <span className="text-xs text-foreground/80 font-medium">{unit}</span>
      </p>
      {note && <p className="mt-1 text-xs text-primary font-medium">{note}</p>}
      <div
        className={`mt-3 inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium w-full ${
          highlight
            ? "bg-primary text-primary-foreground"
            : "bg-foreground/90 text-background"
        }`}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Opening Stripe…
          </>
        ) : (
          <>Start free trial · {label}</>
        )}
      </div>
    </button>
  );
}
